import React, { useCallback, useEffect, useState } from 'react';

import { Button, Modal, Space, Table, message } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { cancelPushSchedule, getPushSchedules } from '../../../hooks/schedules';
import PushScheduleEditModal from './PushScheduleEditModal';
import { formatAudience, formatDateTime, formatRecurrence } from './scheduleColumns';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

/**
 * Traduz por código HTTP, nunca despejando o corpo cru da resposta na tela — mesmo
 * cuidado do PushScheduleEditModal, evitando o parsing de erro por regex que o
 * PushNotificationConfigForm faz sobre o texto do backend.
 */
const describeCancelError = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return T('Este agendamento não existe mais. Atualize a lista.');
  }
  if (status === 409) {
    return T('Este agendamento já foi processado e não pode mais ser cancelado.');
  }
  return T('Não foi possível cancelar o agendamento. Tente novamente.');
};

const PushScheduleTab = ({ active = true, onCountChange }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPushSchedules({ page, size: pageSize, status: 'SCHEDULED' });
      const nextResults = data?.results || [];
      const nextCount = data?.count || 0;
      setResults(nextResults);
      setCount(nextCount);
      if (typeof onCountChange === 'function') onCountChange(nextCount);
    } catch {
      message.error(T('Erro ao carregar agendamentos'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  // A aba nao deve chamar a API enquanto estiver escondida.
  useEffect(() => {
    if (active) fetchSchedules();
  }, [active, fetchSchedules]);

  const handleCancel = (record) => {
    Modal.confirm({
      title: T('Cancelar agendamento?'),
      content: (
        <span>
          {T('Se houver repetição,')} <strong>{T('todas as ocorrências futuras')}</strong>
          {' '}{T('serão canceladas. Essa ação não pode ser desfeita.')}
        </span>
      ),
      okText: T('Cancelar agendamento'),
      okType: 'danger',
      cancelText: T('Voltar'),
      onOk: async () => {
        try {
          await cancelPushSchedule(record.uuid);
          message.success(T('Agendamento cancelado'));
          fetchSchedules();
        } catch (error) {
          message.error(describeCancelError(error));
        }
      },
    });
  };

  const columns = [
    {
      title: T('Data/hora'),
      dataIndex: 'nextRunAt',
      key: 'nextRunAt',
      render: (_, r) => formatDateTime(r.nextRunAt, r.timezone),
      width: 220,
    },
    {
      title: T('Push'),
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: T('Destinatários'),
      key: 'audience',
      render: (_, r) => formatAudience(r),
      width: 220,
    },
    {
      title: T('Repetição'),
      key: 'recurrence',
      render: (_, r) => formatRecurrence(r),
      width: 200,
    },
    {
      title: T('Ações'),
      key: 'action',
      width: 160,
      render: (_, r) => (
        <Space size='small'>
          <Button
            size='small'
            type='link'
            onClick={() => setEditingSchedule(r)}
          >
            {T('Editar')}
          </Button>
          <Button
            size='small'
            type='link'
            danger
            onClick={() => handleCancel(r)}
          >
            {T('Cancelar')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper>
      <Table
        rowKey='uuid'
        columns={columns}
        dataSource={results}
        loading={loading}
        size='small'
        locale={{ emptyText: T('Nenhum push agendado') }}
        pagination={{
          current: page,
          pageSize,
          total: count,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
          showTotal: (total) => `${total} ${T('registros')}`,
        }}
      />

      <PushScheduleEditModal
        schedule={editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSaved={() => {
          setEditingSchedule(null);
          fetchSchedules();
        }}
      />
    </Wrapper>
  );
};

export default PushScheduleTab;
